import { auth } from './firebase.ts';
import { QueryFunctionContext } from '@tanstack/react-query';

export const getCurrentUserId = () => {
  return auth.currentUser?.uid;
};

const fetcher = async (path: string, options: RequestInit) => {
  await auth.currentUser?.reload();
  const token = await auth.currentUser?.getIdToken();
  const authHeader = token
    ? {
        Authorization: `Bearer ${token}`,
      }
    : {};

  return fetch(import.meta.env.VITE_API_URL + path, {
    ...options,
    headers: {
      ...authHeader,
      'Content-Type': 'application/json',
      ...options.headers,
    } as HeadersInit,
  });
};

export const log = async (
  name: string,
  data?: Record<string, string>,
  meta?: Record<string, string>,
) => {
  const res = await fetcher('/sitter_api/log_events', {
    method: 'POST',
    body: JSON.stringify({ name, data, meta }),
  });

  if (res.ok) {
    return await res.json();
  } else {
    console.error(res);
    throw new Error('Failed');
  }
};

type Sitter = {
  id: string;
  name: string;
  email: string;
  university: string;
  avatar: {
    id: string;
    url: string;
  };
};

export const checkAuth = async (): Promise<Sitter | 'invalid sitter' | false> => {
  const res = await fetcher('/sitter_api/self', {
    method: 'GET',
  });

  if (res.ok) {
    return res.json();
  } else {
    try {
      const json = await res.json();
      if (json.message === 'invalid sitter') {
        return 'invalid sitter';
      } else {
        return false;
      }
    } catch (e) {
      console.error(e);
      return false;
    }
  }
};

export const getCurrentSitter = async (): Promise<Sitter | null> => {
  const res = await fetcher('/sitter_api/self', {
    method: 'GET',
  });

  if (res.ok) {
    return await res.json();
  } else {
    return null;
  }
};

export type SittingSchedule = {
  id: string;
  user: {
    id: string;
    profile: {
      name: string | null;
      address: string | null;
    };
  };
  sitter: {
    id: string | null;
    name: string | null;
  };
  record: {
    id: string | null;
  };
  startAt: string;
  durationHours: number;
  memo: string;
  status: 'draft' | 'fixed' | 'done' | 'reported' | 'canceled';
};

export const getAvailableSittingSchedules = async (): Promise<SittingSchedule[] | null> => {
  const res = await fetcher('/sitter_api/sittings?available=true', {
    method: 'GET',
  });

  if (res.ok) {
    return await res.json();
  } else {
    return null;
  }
};

export type SittingRecord = {
  id: string;
  status: 'draft' | 'confirmed';
  children: string[];
  durationHours: number;
  startTime: string;
  memo: string;
  activities: {
    title: string;
    description: string;
    actionId: string;
    actionEpisode: string;
    images: {
      id: string;
      url: string;
    }[];
  }[];
  sitterMessage: string;
  expensePrice: number;
  expenseMemo: string;
  transportationExpensePrice: number;
  transportationExpenseMemo: string;
  transportationExpenseImages: {
    id: string;
    url: string;
  }[];
  token: string;
};

export type SittingScheduleWithReport = SittingSchedule & {
  user: {
    id: string;
    profile: {
      name: string | null;
      address: string | null;
    };
    children: {
      id: string;
      name: string;
    }[];
  };
  record: SittingRecord;
};

export const getMySittingSchedules = async (): Promise<SittingSchedule[] | null> => {
  const res = await fetcher('/sitter_api/sittings', {
    method: 'GET',
  });

  if (res.ok) {
    return await res.json();
  } else {
    if (res.status.toString().startsWith('4')) {
      return null;
    }
    console.error(res);
    throw new Error('Failed');
  }
};

export const getSittingSchedule = async (
  context: QueryFunctionContext,
): Promise<SittingScheduleWithReport | null> => {
  const id = context.queryKey[1] as string;
  const res = await fetcher(`/sitter_api/sittings/${id}`, {
    method: 'GET',
  });

  if (res.ok) {
    return await res.json();
  } else {
    if (res.status.toString().startsWith('4')) {
      return null;
    }
    console.error(res);
    throw new Error('Failed');
  }
};

type ExperienceAction = {
  id: string;
  name: string;
  title: string;
  description: string;
  advice: string;
};

export const searchExperienceActions = async (
  context: QueryFunctionContext,
): Promise<ExperienceAction[] | null> => {
  const query = context.queryKey[1] as string;
  const res = await fetcher(`/sitter_api/actions?q=${query}`, {
    method: 'GET',
  });

  if (res.ok) {
    return await res.json();
  } else {
    if (res.status.toString().startsWith('4')) {
      return null;
    }
    console.error(res);
    throw new Error('Failed');
  }
};

export const createSittingRecord = async (body: Record<string, any>) => {
  const res = await fetcher('/sitter_api/records', {
    method: 'POST',
    body: JSON.stringify(body),
  });

  if (res.ok) {
    return await res.json();
  } else {
    if (res.status.toString().startsWith('4')) {
      return null;
    }
    console.error(res);
    throw new Error('Failed');
  }
};

export const createSitter = async (body: Record<string, any>) => {
  const res = await fetcher('/sitter_api/self', {
    method: 'POST',
    body: JSON.stringify(body),
  });

  if (res.ok) {
    return await res.json();
  } else {
    if (res.status.toString().startsWith('4')) {
      return null;
    }
    console.error(res);
    throw new Error('Failed');
  }
};

export const updateProfile = async (body: Record<string, any>) => {
  const res = await fetcher('/sitter_api/self', {
    method: 'PATCH',
    body: JSON.stringify(body),
  });

  if (res.ok) {
    return await res.json();
  } else {
    if (res.status.toString().startsWith('4')) {
      return null;
    }
    console.error(res);
    throw new Error('Failed');
  }
};
